<template>
  <form
    class="px-4 pt-4"
    v-shortkey="shortkeys"
    @shortkey="triggerShortkey"
    @submit.prevent="handleSubmit"
  >
    <div class="row">
      <div class="col-6">
        <CaseCytologyQuestions v-model="caseQuestions" :caseId="caseDetails.caseId" />
      </div>
      <div class="col-6">
        <text-input
          label="Clinical ICD"
          accessKey="i"
          maxLength="81"
          ref="clinicalICDCode"
          id="clinicalICDCode"
          :validator="$v.specimen.clinicalICDCode"
          v-model="specimen.clinicalICDCode"
          :forceUpperCase="upperCaseClinicalIcd10"
        />
        <div id="editors" class="mt-4" ref="editors" :class="editorClasses">
          <h4 class="mb-3"><u>C</u>linical Text</h4>
          <MacroEnabledEditor
            id="clinical"
            key="clinical"
            :expanded="true"
            name="clinical"
            ref="clinical"
            v-bind="textEditors.clinical"
            v-model="specimen['clinical']"
            @editorReady="handleAutoOpenEditor"
          />
        </div>
      </div>
    </div>
    <div class="d-flex align-items-center justify-content-end mt-3">
      <div>
        <loader v-if="isLoading" size="small" />
        <button type="submit" class="btn btn-primary ml-2" :disabled="formDisabled" id="saveBtn">
          Save
        </button>
      </div>
    </div>
    <modal :status="isMacroDialogOpen" @close="isMacroDialogOpen = false">
      <macro-popup
        @close="isMacroDialogOpen = false"
        :targetType="macroDialogType"
        :targetSpecimen="specimen"
        @macroSelected="macroDialogCallback"
        :dialogFromWysiwyg="macroFromWysiwyg"
      />
    </modal>
  </form>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import UsersAPI from "@/services/users";
import { maxLength } from "vuelidate/lib/validators";
import MacroEnabledEditor from "@/components/common/MacroEnabledEditor";
import eventBus, {
  fromBusEvent,
  OPEN_MACRO_POPUP,
  RESTORE_EDITOR_POSITION,
  SAVE_FROM_CASE_HEADER
} from "../../modules/eventBus";
import { switchMap, tap } from "rxjs/operators";
import { CasesApi } from "../../services";
import TextInput from "@/components/common/TextInput.vue";
import { cloneDeep } from "lodash";
import Modal from "@/components/common/Modal.vue";
import MacroPopup from "@/components/MacroPopup.vue";
import Loader from "@/components/common/Loader.vue";
import { mergeSpecimensWithMacros } from "@/modules/mergeSpecimensWithMacros";
import physicianPopup from "@/mixins/physicianPopup.js";
import { initialEditors } from "@/store/ApplicationSettings";
import {
  CaseEditTypeEnum,
  CaseStatusEnum,
  CytQuestionTypeEnum,
  PhysicianPopupsEnum
} from "@/modules/enums";
import { getAltKeys, dateRangeFilter, removeExtraDivs, logShortKey } from "@/modules/helpers";
import CaseCytologyQuestions from "@/components/forms/CaseCytologyQuestions.vue";
import cytologyService, { CytologyEntities } from "@/services/cytology";

export default {
  name: "CytologySpecimenView",
  components: {
    MacroEnabledEditor,
    TextInput,
    Modal,
    MacroPopup,
    Loader,
    CaseCytologyQuestions
  },
  mixins: [physicianPopup],
  props: {
    value: {
      type: Object
    }
  },
  validations() {
    return {
      specimen: {
        clinicalICDCode: {
          maxLength: maxLength(80)
        }
      }
    };
  },
  data: () => ({
    caseQuestions: {},
    userSearch: UsersAPI.searchStore,
    grosser: null,
    cutter: null,
    isMacroDialogOpen: false,
    isLoading: false,
    macroDialogType: 0,
    labQuestions: {},
    macroFromWysiwyg: false,
    specimen: {},
    cytologyQuestionsStore: cytologyService.createSearch(CytologyEntities.CytQuestions),
    questionResponses: [],
    isModalOpen: false,
    shortkeys: getAltKeys("cis"),
    staticCaseId: null
  }),
  created() {
    this.loadLabQuestions();
  },
  mounted() {
    eventBus.$on(SAVE_FROM_CASE_HEADER, () => this.handleSubmit());
    if (this.specimens?.length) {
      this.specimen = cloneDeep(this.specimens[0]);
    }
  },
  beforeDestroy() {
    this.autoPrintOrders();
    eventBus.$off(SAVE_FROM_CASE_HEADER);
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      currentSpecimen: state => state.accessionStore.currentSpecimen,
      caseDetails: state => state.accessionStore.caseDetails,
      ForceUpperCaseGross: state => state.labSettings.ForceUpperCaseGross,
      ForceUpperCaseSite: state => state.labSettings.ForceUpperCaseSite,
      ForceUpperCaseClinical: state => state.labSettings.ForceUpperCaseClinical,
      ForceUpperCaseCaseNote: state => state.labSettings.ForceUpperCaseCaseNote,
      ForceUpperCaseClinicalICD10: state => state.labSettings.ForceUpperCaseClinicalICD10,
      AdditionalFields: state => state.labSettings.AdditionalFields,
      specimens: state => state.accessionStore.specimens,
      caseEditType: state => state.accessionStore.editType,
      textEditors: state => state.applicationSettings.textEditors || initialEditors,
      casePrefix: state => state.accessionStore.casePrefix,
      autoOpenEditors: state => state.applicationSettings.autoOpenEditors,
      specimenFocus: state => state.labSettings.SpecimenFocus,
      getSiteFromProtocolDescription: state => state.labSettings.GetSiteFromProtocolDescription,
      SpecimenNonDiagnosticChange: state => state.labSettings.SpecimenNonDiagnosticChange,
      currentLab: state => state.currentLab,
      DefaultQtyPiecesPerCassette: state => state.labSettings.DefaultQtyPiecesPerCassette,
      PrintSlidesOnSpecimenSave: state => state.labSettings.PrintSlidesOnSpecimenSave,
      defaultGlassSlidePrinter: state => state.applicationSettings.defaultGlassSlidePrinter,
      FileDropPrintingConfiguration: state => state.labSettings.FileDropPrintingConfiguration,
      defaultCassettePrinter: state => state.applicationSettings.defaultCassettePrinter,
      LabSettingDefaultCassettePrinter: state => state.labSettings.DefaultCassetteProcedurePrinter,
      LabBinPrintOrder: state => state.labSettings.LabBinPrintOrder,
      labSettings: state => state.labSettings,
      shouldPrintOrders: state => state.accessionStore.shouldPrintOrders
    }),
    ...mapGetters("accessionStore", ["isCaseEditable", "isReported"]),
    ...mapGetters(["permissions"]),
    editorClasses() {
      return {
        upperCaseClinical: parseInt(this.ForceUpperCaseClinical) || false
      };
    },
    caseId() {
      return this.caseDetails.caseId;
    },

    upperCaseClinical() {
      if (Number(this.ForceUpperCaseClinical)) {
        return true;
      }
      return false;
    },
    status() {
      return this.caseDetails.status;
    },
    formDisabled() {
      if (this.caseDetails.status === CaseStatusEnum.ReportedPrelim) {
        return !this.permissions.CaseSpecimenCreateEdit;
      }
      if (this.isReported) {
        if (this.isCaseEditable) {
          return (
            !this.SpecimenNonDiagnosticChange &&
            [CaseEditTypeEnum.NonDiagnostic, CaseEditTypeEnum.Billing].includes(this.caseEditType)
          );
        }
        return true;
      } else if (this.isCaseEditable) {
        return !this.permissions.CaseSpecimenCreateEdit;
      } else {
        return !this.isCaseEditable;
      }
    },
    draggableEditors: {
      get() {
        return this.availableEditors;
      },
      set(value) {
        const newEditors = value
          .map((editor, idx) => {
            return {
              ...editor,
              specimenOrder: idx
            };
          })
          .reduce((acc, curr) => {
            acc[curr.name] = curr;
            return acc;
          }, {});
        this.$store.commit("applicationSettings/setEditorsOrder", {
          ...this.textEditors,
          ...newEditors
        });
      }
    },
    isFrozenEnabled() {
      return this.permissions.CaseFieldEditFrozen;
    },
    isProstateProtocol() {
      if (this.specimen.protocolId) {
        const protocol = this.protocolItems.find(e => e.id === this.specimen.protocolId);
        if (protocol) {
          return protocol?.isProstate;
        }
      }
      return false;
    },
    upperCaseClinicalIcd10() {
      return this.ForceUpperCaseClinicalICD10;
    }
  },
  watch: {
    specimen: {
      deep: true,
      handler(nv) {
        this.$store.commit("accessionStore/setCurrentSpecimen", nv);
      }
    },
    grosser(nv) {
      if (nv.id != this.specimen.grosserUserId) {
        this.specimen.grosserUserId = nv.id;
      }
    },
    cutter(nv) {
      if (nv.id != this.specimen.cutterUserId) {
        this.specimen.cutterUserId = nv.id;
      }
    },
    "specimen.cutterUserId": {
      immediate: true,
      handler: function (nv) {
        if (nv && nv != this.cutter?.id) {
          this.userSearch.load({ filter: ["id", "=", nv] }).then(res => {
            this.cutter = res[0];
          });
        }
      }
    },
    "specimen.grosserUserId": {
      immediate: true,
      handler: function (nv) {
        if (nv && nv != this.grosser?.id) {
          this.userSearch.load({ filter: ["id", "=", nv] }).then(res => {
            this.grosser = res[0];
          });
        }
      }
    },
    "specimen.specimenOrder": {
      immediate: true,
      handler: function (nv) {
        if (nv && /[a-z]/i.test(nv)) {
          this.specimen.specimenOrder = nv.toUpperCase();
        }
      }
    },
    "specimen.cassettes": {
      immediate: true,
      handler(nv) {
        if (nv?.length !== this.specimen.numberOfBlocks) {
          this.specimen.numberOfBlocks = nv?.length || 0;
        }
      }
    },
    "caseDetails.caseId": {
      immediate: true,
      handler(nv) {
        if (nv) {
          this.staticCaseId = parseInt(nv);
        }
      }
    }
  },
  subscriptions() {
    const openMacroFromWysiwyg$ = fromBusEvent(OPEN_MACRO_POPUP).pipe(
      tap(({ type }) => {
        this.macroDialogType = type;
        this.isMacroDialogOpen = true;
        this.macroFromWysiwyg = true;
      }),
      switchMap(({ callback }) => {
        return fromBusEvent(RESTORE_EDITOR_POSITION).pipe(
          tap(macros => {
            this.macroFromWysiwyg = false;
            this.macroDialogType = 0;
            this.isMacroDialogOpen = false;
            callback(macros);
            for (const macro of macros) {
              ["diagnosis", "microscopic", "notes", "caseNotes", "gross", "clinical"].forEach(
                property => {
                  if (macro[property]) {
                    this.$refs[property].expand();
                    if (property === "diagnosis") {
                      this.$refs[property].focus();
                      this.$refs[property].$el.scrollIntoView({
                        behavior: "auto",
                        block: "center",
                        inline: "center"
                      });
                    }
                  }
                }
              );
            }
          })
        );
      })
    );

    return {
      openMacroFromWysiwyg$
    };
  },
  methods: {
    async loadLabQuestions() {
      try {
        this.isLoading = true;
        const labQuestions = await this.cytologyQuestionsStore.load({
          filter: dateRangeFilter(),
          sort: [
            {
              selector: "seqNum",
              desc: false
            }
          ]
        });

        if (!labQuestions?.length) return;
        for (const question of labQuestions) {
          if (question.cytQuestionType === "List") {
            const questionDetails = await cytologyService.getSingleEntity(
              CytologyEntities.CytQuestions,
              question.id
            );
            question.options = questionDetails.options;
          }
        }
        this.labQuestions = labQuestions.reduce((acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        }, {});
        await this.loadResponses();
        //! TODO: add error handling
      } finally {
        this.isLoading = false;
      }
    },
    async loadResponses() {
      try {
        this.isLoading = true;
        this.questionResponses = await CasesApi.getCytologyResponses(this.caseId);
        if (this.questionResponses.length > 0) {
          this.caseQuestions = this.questionResponses.reduce((acc, curr) => {
            acc[curr.cytQuestionId] = curr.response;
            return acc;
          }, {});
        }
      } finally {
        this.isLoading = false;
      }
    },
    async handleSaveQuestions() {
      try {
        this.isLoading = true;
        const items = [];
        for (const questionId in this.caseQuestions) {
          const question = this.labQuestions[questionId];
          const response = this.questionResponses.find(
            e => e.cytQuestionId === parseInt(questionId)
          );
          if (!question) continue;
          const item = {
            cytQuestionId: parseInt(questionId),
            response: this.caseQuestions[questionId]
          };
          if (question.cytQuestionType === CytQuestionTypeEnum.List) {
            item.cytQuestionOptionId = this.caseQuestions[questionId];
          }
          if (response) {
            item.id = response?.id;
          }
          items.push(item);
        }
        // const putRecords = items.filter(e => e.id);
        // const postRecords = items.filter(e => !e.id);

        await CasesApi.insertCytologyResponses(this.caseDetails.caseId, {
          id: this.caseDetails.caseId,
          items
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    triggerShortkey(event) {
      logShortKey(this.shortkeys[event.srcKey]);
      switch (event.srcKey) {
        case "i":
          this.$refs.clinicalICDCode.focus();
          break;

        case "c":
          this.$refs.clinical.focus();
          break;
        case "s":
          this.handleSubmit();
          break;
        default:
          break;
      }
    },
    async useMacroOnSpecimen(macros, name) {
      try {
        this.$store.commit("accessionStore/setLoading", true);
        if (name) {
          const [updateSpecimen] = await mergeSpecimensWithMacros(
            [this.specimen],
            macros,
            macros.map(macro => `.${macro.macroName}`).join("")
          );
          for (const prop in updateSpecimen) {
            if (prop !== name) {
              this.specimen[prop] = updateSpecimen[prop];
            }
          }
        }
        for (const macro of macros) {
          if (macro.holdCodes?.length) {
            macro.holdCodes.forEach(holdCodeId => {
              if (Object.hasOwnProperty.call(holdCodeId, "id")) {
                holdCodeId = holdCodeId.id;
              }
              this.$store.dispatch("accessionStore/upsertCaseQuickLink", {
                type: "H",
                tagId: holdCodeId,
                caseId: this.specimen.caseId,
                text: `Added by Macro: ${macro.macroName} on Specimen: ${this.specimen.specimenOrder}`
              });
            });
          }
          if (macro.icdCodes?.length && !this.specimen?.isICDModified) {
            await this.$store
              .dispatch("accessionStore/runIcdEngine", {
                specimen: this.specimen,
                macro
              })
              .then(specimen => {
                this.specimen.icdCodes = specimen.icdCodes;
              });
          }
        }
      } catch (e) {
        console.error("Error loading macro from dialog", e);
        window.notify("Error occured loading macro.", "error");
      } finally {
        this.$store.commit("accessionStore/setLoading", false);
      }
    },
    async macroDialogCallback(macros) {
      this.isMacroDialogOpen = false;
      const updatedSpecimens = await mergeSpecimensWithMacros(
        [this.specimen],
        macros,
        macros.map(macro => `.${macro.macroName}`).join("")
      );
      this.specimen = updatedSpecimens[0];
      await this.useMacroOnSpecimen(macros);
      ["clinical"].forEach(property => {
        if (this.specimen[property]) {
          this.$refs[property].isExpanded = true;
        }
      });
    },

    async handleSubmit() {
      console.log(this.isLoading, this.specimens.length);
      if (this.isLoading) {
        return;
      }
      const specimen = this.specimen;
      if (this.formDisabled) {
        return;
      }
      this.$v.$touch();
      if (this.$v.$invalid) {
        window.notify("Please verify your input and try again.", "warning");
        return;
      }
      if (specimen.numberOfBlocks && !specimen.protocolId) {
        window.alert("A protocol must be selected before increasing number of blocks.");
        return;
      }
      this.isLoading = true;
      try {
        const confirmStatus = await this.getPhysicianPopup(PhysicianPopupsEnum.Specimen, [
          specimen
        ]); //Specimen Save
        if (!confirmStatus) {
          this.isLoading = false;
          window.notify("User cancelled action.", "error");
          return;
        }
        let payload;
        specimen["clinical"] = removeExtraDivs(specimen["clinical"]);

        if (specimen.id) {
          payload = this.$store.dispatch("accessionStore/updateCaseSpecimen", specimen);
        } else {
          payload = this.$store.dispatch("accessionStore/insertCaseSpecimen", specimen);
        }

        const [questionsResponse, specimensResponse] = await Promise.all([
          this.handleSaveQuestions(),
          payload
        ]);
        if (questionsResponse) {
          window.notify("Questions saved successfully.", "success");
        }
        if (specimensResponse) {
          window.notify("Specimen saved successfully.", "success");
        }

        this.$emit("save", payload);
        //! DO we have slices in cytology?
        // if (this.PrintSlidesOnSpecimenSave && !grossedBeforeSave && payload[0]?.firstGrossedOn) {
        //   this.printSlides(payload);
        // }
      } catch (error) {
        window.notify("Error saving specimen", "error");
      }
      this.isLoading = false;
    },

    focusFirstEditor() {
      const firstEditorName = this.availableEditors[0].name;
      const firstEditorRef = this.$refs[firstEditorName][0] || this.$refs[firstEditorName];
      if (firstEditorRef) {
        firstEditorRef.focus();
      }
    },
    handleAutoOpenEditor(editorName) {
      if (this.autoOpenEditors && this.$refs[editorName][0]?.expand) {
        this.$refs[editorName][0].expand();
      }
    },
    autoPrintOrders() {
      if (this.shouldPrintOrders) {
        this.$store.commit("accessionStore/setShouldPrintOrders", false);
        this.$store.dispatch("accessionStore/autoPrintOrders", this.staticCaseId);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .text-uppercase {
  input {
    text-transform: uppercase;
  }
}

::v-deep .text-input {
  min-height: unset;
}
/** Hide accordion in cytology */
::v-deep #clinical {
  #headingOne {
    display: none !important;
  }
  .editor {
    border: none !important;
  }
  .collapse:not(.show) {
    display: block;
  }
}
:v-deep.collapse {
  display: block !important;
}

::v-deep.unlocked {
  &,
  *,
  * > *,
  * {
    cursor: move !important;
  }
}
</style>
